import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import EvaluationLayout from '../../components/EvaluationLayout';
import {
  Search,
  Filter,
  Edit,
  Copy,
  Trash2,
  FileText,
  Code,
  Globe,
  Lock,
  Users,
  Tag,
  RefreshCw,
  MoreVertical,
  Plus,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import Button from '../../components/ui/button';
import Input from '../../components/ui/input';
import Badge from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import DropdownMenu from '../../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import templateService from '../../services/templateService';
import toast from 'react-hot-toast';
import { useAuth } from '../../hooks/useAuth';

const TemplateList = () => {
  const navigate = useNavigate();
  const { user: currentUser, isAdmin } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedVisibility, setSelectedVisibility] = useState('all');
  const [activeTab, setActiveTab] = useState('my-templates');
  const [categories, setCategories] = useState({});
  const [visibilityOptions, setVisibilityOptions] = useState({});
  const [stats, setStats] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [perPage] = useState(15);


  const handleStatCardClick = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
    setSelectedCategory('all');
    setSelectedVisibility('all');
    setCurrentPage(1);
  };

  useEffect(() => {
    // Load metadata and initial templates
    Promise.all([
      templateService.getCategories(),
      templateService.getVisibilityOptions(),
      templateService.getTemplates({ type: 'my-templates' }),
      templateService.getTemplateStatistics()
    ]).then(([categoriesData, visibilityData, templatesData, statsData]) => {
      setCategories(categoriesData);
      setVisibilityOptions(visibilityData);
      setTemplates(templatesData.templates);
      setStats(statsData);
      setLoading(false);
    }).catch(error => {
      console.error('Failed to load initial data:', error);
      toast.error('Failed to load template data');
      setLoading(false);
    });
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const templatesData = await templateService.getTemplates({
        type: activeTab,
        search: searchTerm,
        category: selectedCategory === 'all' ? null : selectedCategory,
        page: currentPage,
        perPage
      });

      console.log(`Loaded ${activeTab} templates:`, {
        count: templatesData.templates.length,
        meta: templatesData.meta
      });

      setTemplates(templatesData.templates);
      setTotalPages(templatesData.meta.last_page);
    } catch (error) {
      console.error(`Failed to load ${activeTab} templates:`, error);

      // More specific error handling
      if (error.response) {
        // Server responded with an error
        switch (error.response.status) {
          case 403:
            toast.error('You do not have permission to view these templates');
            break;
          case 404:
            toast.error('Templates not found');
            break;
          case 500:
            toast.error('Server error. Please try again later.');
            break;
          default:
            toast.error(error.response.data?.message || 'Failed to load templates');
        }
      } else if (error.request) {
        // Request was made but no response received
        toast.error('No response from server. Please check your network connection');
      } else {
        // Something happened in setting up the request
        toast.error('An unexpected error occurred while loading templates');
      }

      // Always set templates to an empty array to prevent undefined errors
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [activeTab, searchTerm, selectedCategory, currentPage, perPage]);

  useEffect(() => {
    // Reload templates when tab changes
    loadTemplates();
  }, [loadTemplates]);

  const handleCloneTemplate = async (templateId) => {
    try {
      const clonedTemplate = await templateService.cloneTemplate(templateId);
      toast.success('Template cloned successfully');

      // Navigate to the newly cloned template's edit page
      navigate(`/templates/${clonedTemplate.id}`);

      // Refresh the list to show updated clone count
      loadTemplates();
    } catch (error) {
      console.error('Failed to clone template:', error);

      // Provide more specific error messages
      if (error.response) {
        // Server responded with an error
        switch (error.response.status) {
          case 403:
            toast.error('You do not have permission to clone this template');
            break;
          case 404:
            toast.error('Template not found');
            break;
          case 422:
            toast.error('Invalid template data');
            break;
          default:
            toast.error(error.message || 'Failed to clone template');
        }
      } else if (error.request) {
        // Request was made but no response received
        toast.error('No response from server. Please check your network connection');
      } else {
        // Something happened in setting up the request
        toast.error('An unexpected error occurred while cloning the template');
      }
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) {
      return;
    }

    try {
      await templateService.deleteTemplate(templateId);
      toast.success('Template deleted successfully');
      loadTemplates(); // Refresh the list
    } catch (error) {
      console.error('Failed to delete template:', error);
      toast.error('Failed to delete template');
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'coding':
        return <Code className="w-4 h-4" />;
      default:
        return <FileText className="w-4 h-4" />;
    }
  };

  const getVisibilityIcon = (visibility) => {
    switch (visibility) {
      case 'public':
        return <Globe className="w-4 h-4 text-green-500" />;
      case 'organization':
        return <Users className="w-4 h-4 text-blue-500" />;
      default:
        return <Lock className="w-4 h-4 text-gray-500" />;
    }
  };

  const getVisibilityBadge = (visibility) => {
    const variants = {
      public: 'secondary',
      organization: 'default',
      private: 'outline'
    };

    return (
      <Badge variant={variants[visibility] || 'outline'} className="flex items-center space-x-1">
        {getVisibilityIcon(visibility)}
        <span>{visibilityOptions[visibility] || visibility}</span>
      </Badge>
    );
  };



  const TemplateCard = ({ template }) => {
    const dropdownItems = [];

    // Clone is always allowed
    dropdownItems.push({
      label: (
        <div className="flex items-center space-x-2">
          <Copy className="w-4 h-4" />
          <span>Clone</span>
        </div>
      ),
      onClick: () => handleCloneTemplate(template.id)
    });

    // Edit only for owner's templates
    if (template.user_id === currentUser?.id) {
      dropdownItems.push({
        label: (
          <div className="flex items-center space-x-2">
            <Edit className="w-4 h-4" />
            <span>Edit</span>
          </div>
        ),
        onClick: () => navigate(`/templates/${template.id}`)
      });
    }

    // Delete for owner's templates or admins
    if (template.user_id === currentUser?.id || isAdmin) {
      dropdownItems.push({
        label: (
          <div className="flex items-center space-x-2 text-destructive">
            <Trash2 className="w-4 h-4" />
            <span>Delete</span>
          </div>
        ),
        onClick: () => handleDeleteTemplate(template.id)
      });
    }

    const handleCardClick = () => {
      if (template.user_id === currentUser?.id) {
        navigate(`/templates/${template.id}`);
      } else {
        // For non-owned templates, navigate to view page (server will enforce access)
        navigate(`/templates/${template.id}`);
      }
    };

    return (
      <Card
        className={`hover:shadow-md transition-shadow cursor-pointer ${template.visibility === 'public' ? 'opacity-80 hover:opacity-100' : ''
          }`}
        onClick={handleCardClick}
      >
        <CardContent className="p-6">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                {getCategoryIcon(template.category)}
                <h3 className="text-lg font-semibold text-foreground">{template.name}</h3>
                {template.is_default && (
                  <Badge variant="default">Default</Badge>
                )}
                {template.visibility === 'public' && (
                  <Badge variant="secondary">Public</Badge>
                )}
              </div>

              <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                {template.description || 'No description provided'}
              </p>

              <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                <div className="flex items-center space-x-1">
                  {getCategoryIcon(template.category)}
                  <span>{categories[template.category] || template.category}</span>
                </div>

                {getVisibilityBadge(template.visibility)}

                {template.clone_count > 0 && (
                  <div className="flex items-center space-x-1">
                    <Copy className="w-4 h-4" />
                    <span>{template.clone_count} clones</span>
                  </div>
                )}
              </div>
            </div>

            <DropdownMenu
              trigger={
                <Button
                  variant="ghost"
                  size="icon"
                >
                  <MoreVertical className="w-4 h-4" />
                </Button>
              }
              items={dropdownItems}
            />
          </div>

          {template.tags && template.tags.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              {template.tags.slice(0, 3).map((tag, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  <Tag className="w-3 h-3 mr-1" />
                  {tag}
                </Badge>
              ))}
              {template.tags.length > 3 && (
                <Badge variant="outline" className="text-xs">
                  +{template.tags.length - 3} more
                </Badge>
              )}
            </div>
          )}

          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>Updated {template.updated_at}</span>
            <div className="flex items-center space-x-4">
              <span>{template.template_data?.messages?.length || 0} messages</span>
              {template.template_data?.evaluation_criteria?.length > 0 && (
                <span>{template.template_data.evaluation_criteria.length} criteria</span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <EvaluationLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Templates</h1>
            <p className="text-muted-foreground mt-1">
              Manage your evaluation templates and browse public templates
            </p>
          </div>
          <div className="flex space-x-3">
            <Button onClick={() => navigate('/templates/create')}>
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>
        </div>

        {/* Stats Cards */}
        {Object.keys(stats).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card onClick={() => handleStatCardClick('my-templates')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{stats.user_templates || 0}</p>
                  <p className="text-sm text-muted-foreground">My Templates</p>
                </div>
              </CardContent>
            </Card>
            <Card onClick={() => handleStatCardClick('public')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{stats.public_templates || 0}</p>
                  <p className="text-sm text-muted-foreground">Public Templates</p>
                </div>
              </CardContent>
            </Card>
            <Card onClick={() => handleStatCardClick('organization')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{stats.organization_templates || 0}</p>
                  <p className="text-sm text-muted-foreground">Organization</p>
                </div>
              </CardContent>
            </Card>
            <Card onClick={() => handleStatCardClick('defaults')} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-2xl font-bold text-foreground">{stats.default_templates || 0}</p>
                  <p className="text-sm text-muted-foreground">Default Templates</p>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search templates..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex items-center space-x-2">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={selectedCategory} onValueChange={setSelectedCategory} className="!w-[250px]">
              <SelectTrigger>
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(categories).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={selectedVisibility} onValueChange={setSelectedVisibility} className="!w-[250px]">
              <SelectTrigger>
                <SelectValue placeholder="Visibility" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Visibility</SelectItem>
                {Object.entries(visibilityOptions).map(([key, label]) => (
                  <SelectItem key={key} value={key}>{label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Template Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="public">Public Templates</TabsTrigger>
            <TabsTrigger value="my-templates">My Templates</TabsTrigger>
            <TabsTrigger value="organization">Organization</TabsTrigger>
          </TabsList>

          <TabsContent value={activeTab} className="mt-6">
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">Loading templates...</p>
              </div>
            ) : templates.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-medium text-foreground mb-2">
                  {searchTerm || selectedCategory !== 'all'
                    ? 'No matching templates'
                    : 'No templates found'
                  }
                </h3>
                <p className="text-muted-foreground mb-4">
                  {searchTerm || selectedCategory !== 'all'
                    ? 'Try adjusting your search or filter criteria'
                    : activeTab === 'my-templates'
                      ? 'Your templates will appear here when available'
                      : activeTab === 'public'
                        ? 'No public templates available. Check back later.'
                        : activeTab === 'organization'
                          ? 'No organization templates found.'
                          : 'No templates available in this category'
                  }
                </p>

              </div>
            ) : (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {templates.map((template) => (
                    <TemplateCard key={template.id} template={template} />
                  ))}
                </div>

                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center space-x-2 mt-8">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="w-4 h-4 mr-2" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </EvaluationLayout>
  );
};

export default TemplateList;
